import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { distinctUntilChanged, filter, skip, switchMap, take, takeUntil } from 'rxjs/operators';

import { AnnouncementNotificationService } from '../services/announcement-notification.service';
import { AnnouncementAttentionItem } from '../models/announcement.models';
import { isAuthCallbackUrl } from '../auth-callback-routes';
import { OAuthService } from '../../../shared/services/oauth.service';
import { AnnouncementGateDialogComponent, AnnouncementGateDialogData } from './announcement-gate-dialog.component';


/**
 * How many times per login session, per item, a "close for now" is allowed to re-arm the gate for the
 * next navigation. An item that keeps failing to load would otherwise re-prompt on every
 * route change; after this many deferrals the reader is left alone until a genuinely new
 * announcement arrives over the hub or they sign in again.
 */
const MAX_DEFERRED_REOPENS = 1;

/**
 * App-shell orchestrator for the High-priority blocking dialog. Registered once next
 * to `ri-announcement-banner` — renders nothing itself, it only decides *when* to open
 * `AnnouncementGateDialogComponent`:
 *
 * - At app start / login: as soon as the post-login attention refresh reports a
 *   blocking item, open immediately.
 * - Mid-session (a High-priority announcement arriving over the hub): never interrupt
 *   whatever the user is doing — wait for the next `NavigationEnd` instead.
 * - Never over an auth callback route (an in-flight OIDC exchange), and never stacked
 *   on top of an already-open instance.
 */
@Component({
  selector: 'ri-announcement-gate',
  standalone: true,
  imports: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './announcement-gate.component.html',
  styleUrls: ['./announcement-gate.component.scss']
})
export class AnnouncementGateComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  /** Re-armed on every hub arrival (and on a login that lands mid auth-callback). */
  private readonly recheckOnNextNavigation$ = new Subject<void>();
  private dialogOpen = false;
  /** Deferred ("close for now") closes per batch head, so one failing item cannot re-prompt forever. */
  private readonly deferredReopens = new Map<number, number>();

  constructor(
    private readonly notifications: AnnouncementNotificationService,
    private readonly oauthService: OAuthService,
    private readonly dialog: MatDialog,
    private readonly router: Router
  ) { }

  ngOnInit(): void {
    // App start / login: open immediately once the post-login attention refresh
    // lands. attention$ is a BehaviorSubject, so the value seen the instant we
    // subscribe is whatever was already there (the empty default, or a stale value
    // left over from stop() on the previous logout) — skip(1) discards that replay
    // and waits for the refresh this very login triggered.
    this.oauthService.loginChanged
      .pipe(distinctUntilChanged(), filter(Boolean), takeUntil(this.destroy$))
      .subscribe(() => {
        this.deferredReopens.clear();
        this.notifications.attention$
          .pipe(skip(1), take(1), takeUntil(this.destroy$))
          .subscribe(attention => this.tryOpen(attention.blocking));
      });

    // Mid-session hub arrival: never interrupt — wait for the next navigation away
    // from an auth callback route. switchMap so a second arrival before the user has
    // navigated anywhere simply re-arms the wait rather than queuing a second one.
    this.notifications.arrivals$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.recheckOnNextNavigation$.next());

    this.recheckOnNextNavigation$
      .pipe(
        switchMap(() => this.router.events.pipe(
          filter((event): event is NavigationEnd => event instanceof NavigationEnd),
          filter(event => !this.isAuthCallbackRoute(event.urlAfterRedirects)),
          take(1)
        )),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.tryOpen(this.notifications.attention.blocking));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private tryOpen(blocking: AnnouncementAttentionItem[]): void {
    if (this.dialogOpen || !blocking.length) return;

    if (this.isAuthCallbackRoute(this.router.url)) {
      // Landed here mid OIDC exchange (e.g. login completed while still on
      // /signin-callback) — wait for the callback to finish navigating away instead.
      this.recheckOnNextNavigation$.next();
      return;
    }

    this.dialogOpen = true;
    const headId = blocking[0].id;
    const ref = this.dialog.open<AnnouncementGateDialogComponent, AnnouncementGateDialogData>(
      AnnouncementGateDialogComponent,
      {
        disableClose: true,
        panelClass: ['mobile-full-dialog'],
        data: { announcementId: blocking[0].id, index: 1, total: blocking.length }
      }
    );

    ref.afterClosed().pipe(take(1), takeUntil(this.destroy$)).subscribe(deferred => {
      this.dialogOpen = false;

      // The dialog was closed via "Sulje toistaiseksi" (load/confirm kept failing) rather
      // than by finishing the batch — nothing was marked seen/acknowledged, so the item
      // is still blocking. Re-arm for the next navigation instead of losing it silently -
      // but only a limited number of times, so an item that can never load does not
      // re-prompt on every route change for the rest of the session.
      if (!deferred) {
        // The batch was finished: whatever failed before has recovered.
        this.deferredReopens.clear();
      } else if ((this.deferredReopens.get(headId) ?? 0) < MAX_DEFERRED_REOPENS) {
        this.deferredReopens.set(headId, (this.deferredReopens.get(headId) ?? 0) + 1);
        this.recheckOnNextNavigation$.next();
      }
    });
  }

  private isAuthCallbackRoute(url: string): boolean {
    return isAuthCallbackUrl(url);
  }
}
